namespace MACRAIL.Business {

export class RegisterEntry {

    private timeIn: Date = undefined;
    private timeOut: Date = undefined;
    private timeLeftRest: Date = undefined;
    private timeReturnRest: Date = undefined;
    private travelIn: number = undefined;
    private travelOut: number = undefined;

    private onSiteWarning: number = undefined;
    private onSiteDanger: number = undefined;
    private doorToDoorWarning: number = undefined;
    private doorToDoorDanger: number = undefined;


    // Time in / out.

    public setTimeIn(newTimeIn: Date): this {

        this.timeIn = newTimeIn;

        return this;
    }

    public getTimeIn(): Date {
        return this.timeIn;
    }

    public hasTimeIn(): boolean {
        return this.timeIn !== undefined;
    }

    public setTimeOut(newTimeOut: Date): this {
        this.timeOut = newTimeOut;

        return this;
    }

    public getTimeOut(): Date {
        return this.timeOut;
    }

    public hasTimeOut(): boolean {
        return this.timeOut !== undefined;
    }


    // Time left rest / Time return rest.

    public setTimeLeftRest(newTimeLeftRest: Date): this {
        this.timeLeftRest = newTimeLeftRest;

        return this;
    }

    public hasTimeLeftRest(): boolean {
        return this.getTimeLeftRest() !== undefined;
    }

    public getTimeLeftRest(): Date {
        if (this.timeLeftRest !== undefined) {
            return this.timeLeftRest;
        }

        if (this.timeIn !== undefined && this.travelIn !== undefined) {
            return new Date(this.timeIn.getTime() - this.travelIn);
        }

        return undefined;
    }

    public setTimeReturnRest(newTimeReturnRest: Date): this {
        this.timeReturnRest = newTimeReturnRest;

        return this;
    }

    public hasTimeReturnRest(): boolean {
        return this.getTimeReturnRest() !== undefined;
    }

    public getTimeReturnRest(): Date {
        if (this.timeReturnRest !== undefined) {
            return this.timeReturnRest;
        }

        if (this.timeOut !== undefined && this.travelOut !== undefined) {
            return new Date(this.timeOut.getTime() + this.travelOut);
        }

        return undefined;
    }

    public setTravelIn(newTravelIn: number): this {
        this.travelIn = newTravelIn;

        return this;
    }

    public hasTravelIn(): boolean {
        return this.getTravelIn() !== undefined;
    }

    public getTravelIn(): number {
        if (this.travelIn !== undefined) {
            return this.travelIn;
        }

        if (this.timeIn !== undefined && this.timeLeftRest !== undefined) {
            let travel = this.timeIn.getTime() - this.timeLeftRest.getTime();

            // Do not allow a negative travel time.
            if (travel >= 0) {
                return travel;
            }
        }

        return undefined;
    }

    public setTravelOut(newTravelOut: number): this {
        this.travelOut = newTravelOut;

        return this;
    }

    public hasTravelOut(): boolean {
        return this.getTravelOut !== undefined;
    }

    public getTravelOut(): number {
        if (this.travelOut !== undefined) {
            return this.travelOut;
        }

        if (this.timeOut !== undefined && this.timeReturnRest !== undefined) {
            let travel = this.timeReturnRest.getTime() - this.timeOut.getTime();

            // Do not allow a negative travel time.
            if (travel >= 0) {
                return travel;
            }
        }

        return undefined;
    }


    // Exceedance get / set.

    public setOnSiteWarning(newOnSiteWarning: number): this {
        this.onSiteWarning = newOnSiteWarning;

        return this;
    }

    public hasOnSiteWarning(): boolean {
        return this.onSiteWarning !== undefined;
    }

    public getOnSiteWarning(): Number {
        return this.onSiteWarning;
    }

    public setOnSiteDanger(newOnSiteDanger: number): this {
        this.onSiteDanger = newOnSiteDanger;

        return this;
    }

    public hasOnSiteDanger(): boolean {
        return this.onSiteDanger !== undefined;
    }

    public getOnSiteDanger(): number {
        return this.onSiteDanger;
    }

    public setDoorToDoorWarning(newDoorToDoorWarning: number): this {
        this.doorToDoorWarning = newDoorToDoorWarning;

        return this;
    }

    public hasDoorToDoorWarning(): boolean {
        return this.doorToDoorWarning !== undefined;
    }

    public getDoorToDoorWarning(): number {
        return this.doorToDoorWarning;
    }

    public setDoorToDoorDanger(newDoorToDoorDanger: number): this {
        this.doorToDoorDanger = newDoorToDoorDanger;

        return this;
    }

    public hasDoorToDoorDanger(): boolean {
        return this.doorToDoorDanger !== undefined;
    }

    public getDoorToDoorDanger(): number {
        return this.doorToDoorDanger;
    }


    // Durations.

    public hasOnSiteDuration(): boolean {
        return this.hasTimeIn() && this.hasTimeOut();
    }

    public getOnSiteDuration(): number {
        if (this.timeIn === undefined || this.timeOut === undefined) {
            return undefined;
        }

        return this.timeOut.getTime() - this.timeIn.getTime();
    }

    public hasDoorToDoorDuration(): boolean {
        return this.hasTimeLeftRest() && this.hasTimeReturnRest();
    }

    public getDoorToDoorDuration(): number {
        let timeLeftRest = this.getTimeLeftRest();
        let timeReturnRest = this.getTimeReturnRest();

        if (timeLeftRest === undefined || timeReturnRest === undefined) {
            return undefined;
        }

        return timeReturnRest.getTime() - timeLeftRest.getTime();
    }


    // Must leave by rules

    public getMustLeaveByForOnSiteRule(): Date {
        if (this.timeIn === undefined || this.onSiteDanger === undefined) {
            return undefined;
        }

        return new Date(this.timeIn.getTime() + this.onSiteDanger);
    }

    public getMustLeaveByForDoorToDoorRule(): Date {
        let timeLeftRest = this.getTimeLeftRest();
        let travelOut = this.getTravelOut() || 0;

        if (!timeLeftRest || this.doorToDoorDanger === undefined) {
            return undefined;
        }

        return new Date(timeLeftRest.getTime() - travelOut + this.doorToDoorDanger);
    }


    // Exceedances

    public isOverOnSiteWarning(): boolean {
        let duration = this.getOnSiteDuration();

        // TODO: Write tests to ensure this truthy checks are correct.
        if (duration && this.onSiteWarning && duration > this.onSiteWarning) {
            return true;
        }

        return false;
    }

    public isOverOnSiteDanger(): boolean {
        let duration = this.getOnSiteDuration();

        if (duration && this.onSiteDanger && duration > this.onSiteDanger) {
            return true;
        }

        return false;
    }

    public isOverDoorToDoorWarning(): boolean {
        let duration = this.getDoorToDoorDuration();

        if (duration && this.doorToDoorWarning && duration > this.doorToDoorWarning) {
            return true;
        }

        return false;
    }

    public isOverDoorToDoorDanger(): boolean {
        let duration = this.getDoorToDoorDuration();

        if (duration && this.doorToDoorDanger && duration > this.doorToDoorDanger) {
            return true;
        }

        return false;
    }
}

}
